<template>
  <div class="purchases-list">
    <!-- Filters -->
    <div class="filters">
      <select v-model="filterStatus" @change="onFilterChange" class="filter-select">
        <option value="">{{ $t('bo.billing.allStatuses') }}</option>
        <option value="pending">{{ $t('bo.billing.statusPending') }}</option>
        <option value="succeeded">{{ $t('bo.billing.statusSucceeded') }}</option>
        <option value="failed">{{ $t('bo.billing.statusFailed') }}</option>
        <option value="cancelled">{{ $t('bo.billing.statusCancelled') }}</option>
      </select>
    </div>

    <div v-if="loading" class="loading">{{ $t('bo.common.loading') }}</div>
    <div v-else-if="error" class="error-msg">{{ error }}</div>
    <div v-else-if="purchases.length === 0" class="empty">{{ $t('bo.billing.noPurchases') }}</div>

    <table v-else class="table">
      <thead>
        <tr>
          <th>{{ $t('bo.billing.colCompany') }}</th>
          <th>{{ $t('bo.billing.colCredits') }}</th>
          <th>{{ $t('bo.billing.colPrice') }}</th>
          <th>{{ $t('bo.billing.colPaymentStatus') }}</th>
          <th>{{ $t('bo.billing.colProvider') }}</th>
          <th>{{ $t('bo.billing.colReference') }}</th>
          <th>{{ $t('bo.billing.colBuyer') }}</th>
          <th>{{ $t('bo.billing.colDate') }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="p in purchases" :key="p.id">
          <td class="col-company">{{ p.company?.name || '—' }}</td>
          <td><strong>{{ p.credit_amount }}</strong></td>
          <td>{{ (p.price_amount / 100).toFixed(2) }} {{ p.currency }}</td>
          <td><span :class="['status-badge', `status-${p.payment_status}`]">{{ $t(`bo.billing.status${capitalize(p.payment_status)}`) }}</span></td>
          <td><span class="chip-gray">{{ p.payment_provider }}</span></td>
          <td class="col-ref">{{ p.payment_reference || '—' }}</td>
          <td class="col-buyer">{{ p.buyer?.email || '—' }}</td>
          <td class="col-date">{{ formatDate(p.created_at) }}</td>
        </tr>
      </tbody>
    </table>

    <!-- Pagination -->
    <div v-if="pagination && pagination.last_page > 1" class="pagination">
      <button :disabled="pagination.current_page === 1" @click="changePage(pagination.current_page - 1)" class="page-btn">&larr;</button>
      <span class="page-info">{{ pagination.current_page }} / {{ pagination.last_page }}</span>
      <button :disabled="pagination.current_page === pagination.last_page" @click="changePage(pagination.current_page + 1)" class="page-btn">&rarr;</button>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import billingService from '@/services/billingService'

const { t } = useI18n()

const purchases = ref([])
const loading = ref(false)
const error = ref('')
const pagination = ref(null)
const currentPage = ref(1)
const filterStatus = ref('')

const load = async () => {
  loading.value = true
  error.value = ''
  try {
    const params = { page: currentPage.value }
    if (filterStatus.value) params.status = filterStatus.value
    const res = await billingService.getPurchases(params)
    const data = res.data?.data
    purchases.value = data?.data || []
    pagination.value = data ? { current_page: data.current_page, last_page: data.last_page, total: data.total } : null
  } catch {
    error.value = t('bo.billing.failedLoad')
  } finally {
    loading.value = false
  }
}

const onFilterChange = () => { currentPage.value = 1; load() }
const changePage = (page) => { currentPage.value = page; load() }
const capitalize = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : ''
const formatDate = (d) => d ? new Date(d).toLocaleString() : '—'

onMounted(load)
</script>

<style scoped>
.purchases-list {
  background: white;
  border-radius: 0.75rem;
  border: 1px solid #e5e7eb;
  overflow: hidden;
}

.filters {
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #e5e7eb;
}

.filter-select {
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  background: white;
}

.loading, .empty {
  padding: 3rem;
  text-align: center;
  color: #6b7280;
}

.error-msg { padding: 1rem 1.25rem; color: #dc2626; }

.table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.table th {
  padding: 0.75rem 1rem;
  text-align: left;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6b7280;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}

.table td {
  padding: 0.875rem 1rem;
  border-bottom: 1px solid #f3f4f6;
  vertical-align: middle;
}

.table tr:last-child td { border-bottom: none; }

.col-company { font-weight: 600; color: #1f2937; }
.col-ref { font-family: monospace; font-size: 0.75rem; color: #6b7280; max-width: 160px; overflow: hidden; text-overflow: ellipsis; }
.col-buyer { color: #6b7280; font-size: 0.8rem; }
.col-date { color: #9ca3af; white-space: nowrap; font-size: 0.8rem; }

.status-badge {
  display: inline-block;
  padding: 0.2rem 0.6rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
}

.status-Pending { background: #fef3c7; color: #92400e; }
.status-Succeeded { background: #d1fae5; color: #065f46; }
.status-Failed { background: #fee2e2; color: #991b1b; }
.status-Cancelled { background: #f3f4f6; color: #6b7280; }

.chip-gray {
  display: inline-block;
  padding: 0.15rem 0.5rem;
  background: #f3f4f6;
  color: #374151;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
}

.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 1rem;
  border-top: 1px solid #e5e7eb;
}

.page-btn {
  padding: 0.375rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  background: white;
  cursor: pointer;
  font-size: 0.875rem;
}

.page-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.page-btn:hover:not(:disabled) { background: #f9fafb; }
.page-info { font-size: 0.875rem; color: #6b7280; }
</style>
